/**
 * The right-hand side of a filter condition.
 *
 * One component, used by the step filter and by the advanced builder, because
 * the two were asking the same question with different answers: the step
 * filter offered chips for a status and the builder offered a text box, so the
 * same filter was easy in one place and a guessing game in the other.
 *
 * What it offers follows the column:
 *
 *   an enum   → the values that column actually holds, as chips or a select;
 *   a date    → the seventeen windows in `date-periods.ts`, or a custom range;
 *   a boolean → yes or no;
 *   a number  → a number box;
 *   anything else → a text box.
 *
 * "The values that column actually holds" is asked of the data rather than
 * read off an enum in /shared, because the useful version includes the columns
 * that are not enums at all — the branches somebody actually operates — and a
 * hard-coded list would offer statuses no job has ever been in.
 */
import { useEffect, useState } from 'react'
import {
  datePeriod, isPreset, type FilterCondition, type FilterOperator,
} from '@rowted/shared'
import { MenuMultiSelect, MenuSelect } from './menu-select.js'
import { DateField } from './date-field.js'
import { cn } from '../lib/cn.js'
import type { ListFieldDef, UseListResult } from '../lib/use-list.js'

const CONTROL = 'h-8 rounded-field border border-line bg-surface px-2 text-cell text-text'

/*
 * The value fills its row, whichever control it turns out to be.
 *
 * Step three used to be whatever width its control happened to want — a 100px
 * yes/no beside a 170px dropdown beside a full-width set of chips — under two
 * steps that were both the width of the panel. One class, applied to every
 * branch below, is the whole fix; the alternative is remembering it in nine
 * places and getting it right in seven.
 */
const FILL = 'w-full min-w-0 flex-1'

/** The values a column holds, fetched once per column and remembered. */
export function useDistinct(list: UseListResult, field: ListFieldDef | undefined) {
  const [values, setValues] = useState<string[]>([])
  const name = field?.name
  const isEnum = field?.kind === 'enum'

  useEffect(() => {
    if (!name || !isEnum) { setValues([]); return }
    let cancelled = false
    list.distinct(name)
      .then((result) => { if (!cancelled) setValues(result) })
      .catch(() => { if (!cancelled) setValues([]) })
    return () => { cancelled = true }
  }, [name, isEnum])

  return values
}

/** "in_progress" as it is stored; "In progress" as it reads. */
export const humanValue = (value: unknown): string => String(value ?? '')
  .replace(/_/g, ' ')
  .replace(/^./, (first) => first.toUpperCase())

export interface FilterValueProps {
  list: UseListResult
  field: ListFieldDef | undefined
  condition: FilterCondition
  onChange: (value: unknown) => void
}

/**
 * A date window is **not** here any more (v3 §5.3).
 *
 * `in_period` used to render `PeriodPicker` — a seventeen-item dropdown with
 * two date boxes behind a "These dates" option — and the builder's row was
 * the only place in the app with that control. It is `DateWindowButton` now,
 * the same picker the quick filter opens, so there is one answer to "which
 * two days" and `layout` has no caller left to need it.
 */
export function FilterValue({
  list, field, condition, onChange,
}: FilterValueProps) {
  const options = useDistinct(list, field)
  const multi = condition.op === 'in' || condition.op === 'not_in'

  if (field?.kind === 'boolean') {
    return (
      <MenuSelect
        label="Value"
        value={String(condition.value ?? 'true')}
        onChange={(value) => onChange(value === 'true')}
        options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
        minWidth={120}
        className={FILL}
      />
    )
  }

  if (field?.kind === 'enum' && options.length > 0) {
    const picked = Array.isArray(condition.value)
      ? condition.value.map(String)
      : condition.value === undefined || condition.value === ''
        ? []
        : [String(condition.value)]

    /*
     * Several values is a dropdown of checkboxes, not an open box listing every
     * one. "Status is any of" on a list with eleven statuses used to unroll the
     * whole set into the row and push the rest of the condition — and the Apply
     * button — off the bottom of the dialogue.
     */
    if (multi) {
      return (
        <MenuMultiSelect
          label="Value"
          values={picked}
          onChange={onChange}
          options={options.map((option) => ({ value: option, label: humanValue(option) }))}
          placeholder="Choose values"
          searchPlaceholder="Find a value"
          minWidth={220}
          className={FILL}
        />
      )
    }

    return (
      <MenuSelect
        label="Value"
        value={picked[0] ?? null}
        onChange={(value) => onChange(value ?? '')}
        options={options.map((option) => ({ value: option, label: humanValue(option) }))}
        placeholder="Choose…"
        searchPlaceholder="Find a value"
        minWidth={220}
        className={FILL}
      />
    )
  }

  const isDate = field?.kind === 'date' || field?.kind === 'datetime'

  if (condition.op === 'between') {
    const range = Array.isArray(condition.value) ? condition.value : ['', '']
    /*
     * Two halves of one row, not two 110px boxes with space left over. The
     * "and" between them is `shrink-0`, so the fields divide whatever is left
     * evenly however narrow the panel gets.
     */
    return (
      <span className={cn('flex items-center gap-1.5', FILL)}>
        {isDate
          ? (
            <DateField
              label="From"
              value={String(range[0] ?? '')}
              onChange={(next) => onChange([next, range[1] ?? ''])}
              className="min-w-0 flex-1"
            />
          )
          : (
            <input
              aria-label="From"
              value={String(range[0] ?? '')}
              onChange={(event) => onChange([event.target.value, range[1] ?? ''])}
              placeholder="From"
              className={cn(CONTROL, 'min-w-0 flex-1')}
            />
          )}
        <span className="shrink-0 text-sub text-text-muted">and</span>
        {isDate
          ? (
            <DateField
              label="To"
              value={String(range[1] ?? '')}
              onChange={(next) => onChange([range[0] ?? '', next])}
              className="min-w-0 flex-1"
            />
          )
          : (
            <input
              aria-label="To"
              value={String(range[1] ?? '')}
              onChange={(event) => onChange([range[0] ?? '', event.target.value])}
              placeholder="To"
              className={cn(CONTROL, 'min-w-0 flex-1')}
            />
          )}
      </span>
    )
  }

  /*
   * A date compared with one date — "is at least", "is at most", "is", "is
   * not". These used to fall through to the text box below, so the control
   * for "due on or after" was an empty rectangle with no hint that it wanted
   * a date, let alone which way round.
   */
  if (isDate && !multi) {
    return (
      <DateField
        label="Value"
        value={String(condition.value ?? '')}
        onChange={onChange}
        className={FILL}
      />
    )
  }

  const numeric = field?.kind === 'number' || field?.kind === 'money'

  return (
    <input
      aria-label="Value"
      type={numeric ? 'number' : 'text'}
      value={Array.isArray(condition.value)
        ? condition.value.join(', ')
        : String(condition.value ?? '')}
      onChange={(event) => onChange(multi
        ? event.target.value.split(',').map((part) => part.trim()).filter(Boolean)
        : event.target.value)}
      placeholder={numeric ? '0' : 'Value'}
      className={cn(CONTROL, FILL, numeric && 'tnum')}
    />
  )
}

/** Whether a condition has enough on its right-hand side to be applied. */
export function valueReady(condition: FilterCondition, nullary: boolean): boolean {
  if (nullary) return true
  if (condition.op === 'in_period') {
    const period = datePeriod.safeParse(condition.value)
    return period.success && (isPreset(period.data)
      || Boolean(period.data.from && period.data.to))
  }
  if (condition.op === 'between') {
    const range = Array.isArray(condition.value) ? condition.value : []
    return Boolean(range[0]) && Boolean(range[1])
  }
  if (Array.isArray(condition.value)) return condition.value.length > 0
  if (typeof condition.value === 'boolean') return true
  return String(condition.value ?? '').trim().length > 0
}

/** What a new condition on this column should start as. */
export function blankCondition(field: ListFieldDef, op: FilterOperator): FilterCondition {
  if (op === 'in_period') return { field: field.name, op, value: { preset: 'last_30_days' } }
  if (field.kind === 'boolean') return { field: field.name, op, value: true }
  return { field: field.name, op, value: op === 'in' || op === 'not_in' ? [] : '' }
}
